import React, { createContext, useCallback, useContext, useMemo, useState, type PropsWithChildren } from 'react';
import { useMediaQuery } from '@mui/material';

/** The app's left panel: Unity's navigation, and inside a report its pages. Hidden, it is gone (no rail). */
export const APP_PANEL = { defaultWidth: 248, minWidth: 200, maxWidth: 420 };
const STORAGE_KEY = 'unity-app-panel';
const NARROW_QUERY = '(max-width: 899.95px)';

const clampWidth = (width: number) => Math.round(Math.min(APP_PANEL.maxWidth, Math.max(APP_PANEL.minWidth, width)));

// A per-browser convenience: the panel works the same when storage is unavailable.
function readSaved(): { open: boolean; width: number } {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        return { open: saved.open !== false, width: typeof saved.width === 'number' ? clampWidth(saved.width) : APP_PANEL.defaultWidth };
    } catch { return { open: true, width: APP_PANEL.defaultWidth }; }
}
function save(value: { open: boolean; width: number }) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* Storage is optional. */ }
}

interface AppPanelState {
    /** Whether the panel shows. On narrow screens it floats over the page and starts hidden. */
    open: boolean;
    /** The panel floats over the page instead of taking room beside it. */
    overlay: boolean;
    width: number;
    /** The room the panel takes beside the page: 0 when hidden or floating. */
    dockedWidth: number;
    toggle: () => void;
    close: () => void;
    setWidth: (width: number) => void;
}

const AppPanelContext = createContext<AppPanelState | null>(null);

export function AppPanelProvider({ children }: PropsWithChildren) {
    const [saved, setSaved] = useState(readSaved);
    const overlay = useMediaQuery(NARROW_QUERY);
    const [overlayOpen, setOverlayOpen] = useState(false);
    const update = useCallback((change: Partial<{ open: boolean; width: number }>) => setSaved(previous => {
        const next = { ...previous, ...change };
        save(next);
        return next;
    }), []);
    const toggle = useCallback(() => {
        if (overlay) setOverlayOpen(value => !value);
        else update({ open: !saved.open });
    }, [overlay, saved.open, update]);
    const close = useCallback(() => {
        if (overlay) setOverlayOpen(false);
        else update({ open: false });
    }, [overlay, update]);
    const setWidth = useCallback((width: number) => update({ width: clampWidth(width) }), [update]);
    const open = overlay ? overlayOpen : saved.open;
    const value = useMemo<AppPanelState>(() => ({
        open, overlay, width: saved.width, dockedWidth: open && !overlay ? saved.width : 0, toggle, close, setWidth,
    }), [open, overlay, saved.width, toggle, close, setWidth]);
    return <AppPanelContext.Provider value={value}>{children}</AppPanelContext.Provider>;
}

export function useAppPanel(): AppPanelState {
    const value = useContext(AppPanelContext);
    if (!value) throw new Error('useAppPanel must be used within an AppPanelProvider');
    return value;
}
